<script>
  import { currentScreen, currentUser } from '../store.js';
  import { onMount } from 'svelte';
  import {
    fetchMyGlobalTeams, createGlobalTeam, searchPlayerByUsername,
    addPlayerToTeam, removePlayerFromTeam, deleteGlobalTeam
  } from '../../services/cloud-sync.js';
  import { toast, confirmDialog } from '../../ui/dialogs.js';
  import { teams as defaultTeams } from '../../teams-data.js';

  // ── Admin: one-tap migration of the 12 default FFPL teams to Global Teams ──
  const ADMIN_EMAIL = 'gkunique001@gmail.com';
  $: isAdmin = $currentUser?.email === ADMIN_EMAIL;
  let isMigrating = false;

  function teamHandleFor(name) {
    return String(name).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 20);
  }

  async function migrateDefaultTeams() {
    if (!(await confirmDialog(`Saari ${defaultTeams.length} default FFPL teams ko Global Teams me add karein? Har team ka @handle banega aur captain + last-played players guest ke roop me add honge.`, { title: 'Migrate FFPL teams' }))) return;
    isMigrating = true;
    let created = 0, skipped = 0;
    for (const t of defaultTeams) {
      const handle = teamHandleFor(t.name);
      try {
        const team = await createGlobalTeam(t.name, handle);
        // Roster: captain first, then the team's last-played XI from this phone.
        const names = [t.captain];
        try {
          const lastXI = JSON.parse(localStorage.getItem('cs_lastXI_' + t.name) || '[]');
          lastXI.forEach(n => { if (n && !names.includes(n)) names.push(n); });
        } catch (_) {}
        for (const playerName of names) {
          try { await addPlayerToTeam(team.id, { uid: null, name: playerName }); } catch (_) {}
        }
        created++;
      } catch (error) {
        skipped++; // handle already taken = already migrated — fine
        console.warn(`[Migrate] ${t.name} (@${handle}):`, error?.message || error);
      }
    }
    isMigrating = false;
    toast(`✅ Migration done — ${created} created${skipped ? `, ${skipped} pehle se the` : ''}.`, 'success');
    await loadCloudTeams();
  }

  let view = 'list'; // 'list', 'create', 'manage'
  let myTeams = [];
  let isLoading = false;

  // Create form
  let newTeamName = "";
  let newTeamUsername = "";

  // Manage Team
  let activeTeam = null;
  let playerSearchQuery = "";
  let searchResult = null;
  let isSearching = false;
  let guestName = "";

  $: isCaptain = activeTeam && $currentUser && activeTeam.captainUid === $currentUser.uid;

  onMount(async () => {
    if (!$currentUser || !$currentUser.uid) {
      toast("Please login to access Global Teams", "error");
      currentScreen.set('home');
      return;
    }
    await loadCloudTeams();
  });

  async function loadCloudTeams() {
    isLoading = true;
    try {
      myTeams = await fetchMyGlobalTeams();
    } catch (e) {
      console.error(e);
      toast("Failed to load cloud teams", "error");
    }
    isLoading = false;
  }

  function goBack() {
    if (view !== 'list') {
      view = 'list';
      activeTeam = null;
    } else {
      currentScreen.set('home');
    }
  }

  async function handleCreateTeam() {
    isLoading = true;
    try {
      const team = await createGlobalTeam(newTeamName, newTeamUsername);
      toast(`Team @${team.username} created!`, "success");
      newTeamName = ""; newTeamUsername = "";
      await loadCloudTeams();
      view = 'list';
    } catch (error) {
      toast(error.message || "Error creating team", "error");
    }
    isLoading = false;
  }

  function manageTeam(team) {
    activeTeam = team;
    view = 'manage';
    playerSearchQuery = "";
    searchResult = null;
    guestName = "";
  }

  async function searchPlayer() {
    if (!playerSearchQuery.trim()) return;
    searchResult = null;
    isSearching = true;
    try {
      const p = await searchPlayerByUsername(playerSearchQuery);
      if (p) searchResult = p;
      else toast("Player not found — check the @username or email.", "error");
    } catch (e) {
      toast("Search failed", "error");
    }
    isSearching = false;
  }

  async function addSearchedPlayer() {
    if (!searchResult || !activeTeam) return;
    try {
      await addPlayerToTeam(activeTeam.id, {
        uid: searchResult.uid,
        name: searchResult.displayName || searchResult.username,
        username: searchResult.username || '',
        photo: searchResult.photo || ''
      });
      activeTeam.roster = [...(activeTeam.roster || []), {
        uid: searchResult.uid,
        name: searchResult.displayName || searchResult.username,
        username: searchResult.username || '',
        photo: searchResult.photo || ''
      }];
      toast(`${searchResult.displayName || searchResult.username} added!`, "success");
      searchResult = null;
      playerSearchQuery = "";
    } catch (error) {
      toast(error.message || "Failed to add player", "error");
    }
  }

  async function addGuestPlayer() {
    const name = guestName.trim();
    if (!name || !activeTeam) return;
    try {
      await addPlayerToTeam(activeTeam.id, { uid: null, name });
      activeTeam.roster = [...(activeTeam.roster || []), { uid: null, name, username: '', photo: '' }];
      toast(`${name} added as guest player.`, "success");
      guestName = "";
    } catch (error) {
      toast(error.message || "Failed to add player", "error");
    }
  }

  async function removePlayer(player) {
    if (!activeTeam) return;
    if (!(await confirmDialog(`Remove ${player.name} from the roster?`, { title: 'Remove player' }))) return;
    try {
      await removePlayerFromTeam(activeTeam.id, player.name);
      activeTeam.roster = activeTeam.roster.filter(r => r.name !== player.name);
      toast(`${player.name} removed.`, "success");
    } catch (error) {
      toast(error.message || "Failed to remove", "error");
    }
  }

  async function handleDeleteTeam() {
    if (!activeTeam) return;
    if (!(await confirmDialog(`Delete @${activeTeam.username} permanently? The handle will be freed.`, { title: 'Delete team', danger: true }))) return;
    try {
      await deleteGlobalTeam(activeTeam);
      toast("Team deleted.", "success");
      activeTeam = null;
      view = 'list';
      await loadCloudTeams();
    } catch (error) {
      toast(error.message || "Failed to delete team", "error");
    }
  }

  async function shareHandle(team) {
    const text = `🏏 Challenge my team "${team.name}" on FFPL Cricket Scorer!\nMatch setup me import karo: @${team.username}`;
    if (navigator.share) {
      navigator.share({ text }).catch(() => {});
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast('Team handle copied — WhatsApp par bhejo!', 'success');
      } catch (_) {
        toast(`Team handle: @${team.username}`, 'success');
      }
    }
  }
</script>

<div class="screen" style="overflow-y:auto; padding-bottom:60px;">
  <button class="btn btn-ghost" on:click={goBack}>← Back</button>

  {#if view === 'list'}
    <div style="display:flex; justify-content:space-between; align-items:center; margin-top:10px;">
      <h1>🌐 Global Teams</h1>
      <button class="btn btn-primary" style="padding:6px 12px; font-size:13px;" on:click={() => view = 'create'}>+ New Team</button>
    </div>

    <p class="muted" style="font-size:12px; margin-top:8px;">
      Apni team ka unique <b>@handle</b> banao. Dusri team se match karna ho to Match Setup me unka handle daalo — poori squad turant import ho jayegi, aur linked players ke stats har phone par sync honge.
    </p>

    {#if isAdmin}
      <button class="btn btn-ghost btn-block" style="margin-top:10px; border-color:#f59e0b; color:#fbbf24; font-size:12px; font-weight:800;" on:click={migrateDefaultTeams} disabled={isMigrating}>
        {isMigrating ? 'Migrating FFPL teams…' : '⬆ MIGRATE 12 DEFAULT FFPL TEAMS TO GLOBAL (admin)'}
      </button>
    {/if}

    {#if isLoading}
      <p style="text-align:center; padding:20px;">Loading...</p>
    {:else if myTeams.length === 0}
      <div class="card" style="margin-top:20px; text-align:center; padding:30px 10px;">
        <h3 style="color:#fbbf24;">No Global Teams Yet</h3>
        <p class="muted" style="font-size:13px;">Create your team, add players by @username, and share your handle with rival teams.</p>
        <button class="btn btn-primary" style="margin-top:10px;" on:click={() => view = 'create'}>Create One Now</button>
      </div>
    {:else}
      {#each myTeams as t, i_each (t.id || t.name || i_each)}
        <div class="card" style="margin-top:10px; display:flex; justify-content:space-between; align-items:center;">
          <div style="min-width:0;">
            <div style="font-weight:800; color:#fbbf24; font-size:16px;">{t.name}</div>
            <div class="meta" style="font-size:12px;">@{t.username} {t.captainUid === $currentUser?.uid ? '· 👑 You are captain' : ''}</div>
            <div class="meta" style="font-size:11px; margin-top:4px;">🛡️ {t.roster?.length || 1} players</div>
          </div>
          <div style="display:flex; gap:6px; flex-shrink:0;">
            <button class="btn btn-ghost" style="padding:6px 10px; font-size:12px;" on:click={() => shareHandle(t)}>Share</button>
            <button class="btn btn-ghost" style="padding:6px 10px; font-size:12px;" on:click={() => manageTeam(t)}>Manage</button>
          </div>
        </div>
      {/each}
    {/if}

  {:else if view === 'create'}
    <h1 style="margin-top:10px;">Create Global Team</h1>
    <div class="card" style="margin-top:10px;">
      <div class="field">
        <label for="new-team-name">Team Name</label>
        <input id="new-team-name" type="text" bind:value={newTeamName} maxlength="30" placeholder="e.g. FFPL Tigers" style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;" />
      </div>
      <div class="field" style="margin-top:12px;">
        <label for="new-team-handle">Team Handle (unique, permanent)</label>
        <input id="new-team-handle" type="text" bind:value={newTeamUsername} maxlength="20" placeholder="e.g. ffpl_tigers" style="width:100%; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;" />
        <div class="muted" style="font-size:11px; margin-top:4px;">3-20 characters — a-z, 0-9 and _ only. Rival teams will import your squad with this handle.</div>
      </div>
      <button class="btn btn-primary btn-block" style="margin-top:20px;" on:click={handleCreateTeam} disabled={isLoading}>
        {isLoading ? 'Creating...' : 'Create Cloud Team'}
      </button>
    </div>

  {:else if view === 'manage' && activeTeam}
    <h1 style="margin-top:10px;">{activeTeam.name}</h1>
    <p class="muted" style="font-size:12px;">@{activeTeam.username} · {isCaptain ? '👑 You are captain' : 'You are a squad member'}</p>

    {#if isCaptain}
      <div class="card" style="margin-top:16px;">
        <h3 style="margin-top:0;">Add Registered Player</h3>
        <div style="display:flex; gap:8px; margin-top:8px;">
          <input type="text" bind:value={playerSearchQuery} placeholder="@username or email..." style="flex:1; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;" on:keydown={(e) => e.key === 'Enter' && searchPlayer()} />
          <button class="btn btn-ghost" on:click={searchPlayer} disabled={isSearching}>{isSearching ? '...' : 'Search'}</button>
        </div>

        {#if searchResult}
          <div style="margin-top:12px; padding:12px; background:#1e293b; border-radius:8px; display:flex; align-items:center; justify-content:space-between;">
            <div style="display:flex; align-items:center; gap:10px; min-width:0;">
              {#if searchResult.photo}
                <img src={searchResult.photo} alt="avatar" style="width:36px; height:36px; border-radius:50%; object-fit:cover;" />
              {:else}
                <div style="width:36px; height:36px; border-radius:50%; background:#334155; display:flex; align-items:center; justify-content:center;">🏏</div>
              {/if}
              <div style="min-width:0;">
                <div style="font-weight:bold; font-size:14px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">{searchResult.displayName || searchResult.username}</div>
                <div style="font-size:11px; color:#9ca3af;">@{searchResult.username}</div>
              </div>
            </div>
            <button class="btn btn-primary" style="padding:6px 12px; font-size:12px; flex-shrink:0;" on:click={addSearchedPlayer}>Add</button>
          </div>
        {/if}

        <div style="margin-top:14px; border-top:1px dashed #334155; padding-top:12px;">
          <div class="muted" style="font-size:11px; margin-bottom:6px;">Player app par nahi hai? Guest ke roop me naam se add karo:</div>
          <div style="display:flex; gap:8px;">
            <input type="text" bind:value={guestName} maxlength="30" placeholder="Guest player name" style="flex:1; padding:10px; background:#0f172a; border:1px solid #334155; color:white; border-radius:6px;" on:keydown={(e) => e.key === 'Enter' && addGuestPlayer()} />
            <button class="btn btn-ghost" on:click={addGuestPlayer}>+ Guest</button>
          </div>
        </div>
      </div>
    {/if}

    <div class="card" style="margin-top:16px;">
      <h3 style="margin-top:0;">Squad ({activeTeam.roster?.length || 0})</h3>
      {#if !activeTeam.roster || activeTeam.roster.length === 0}
        <p class="muted" style="font-size:12px;">No players added yet.</p>
      {:else}
        {#each activeTeam.roster as player (player.name)}
          <div style="padding:8px 0; border-bottom:1px solid #1e293b; display:flex; align-items:center; gap:10px;">
            {#if player.photo}
              <img src={player.photo} alt="avatar" style="width:30px; height:30px; border-radius:50%; object-fit:cover;" />
            {:else}
              <div style="width:30px; height:30px; border-radius:50%; background:#334155; display:flex; align-items:center; justify-content:center; font-size:14px;">🏏</div>
            {/if}
            <div style="flex:1; min-width:0;">
              <div style="font-size:14px; font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">
                {player.name}
                {#if player.uid === activeTeam.captainUid}<span style="font-size:11px; color:#fbbf24;"> 👑</span>{/if}
              </div>
              <div style="font-size:10px; color:#64748b;">
                {player.username ? '@' + player.username : player.uid ? 'linked' : 'guest player'}
              </div>
            </div>
            {#if isCaptain && player.uid !== activeTeam.captainUid}
              <button class="btn btn-ghost" style="padding:4px 10px; font-size:11px; color:#f87171; border-color:#7f1d1d;" on:click={() => removePlayer(player)}>Remove</button>
            {/if}
          </div>
        {/each}
      {/if}
      <p class="muted" style="font-size:11px; margin-top:10px;">💡 Match Setup me "Import Cloud Team" dabao aur <b>@{activeTeam.username}</b> daalo — ye squad turant load ho jayegi.</p>
    </div>

    {#if isCaptain}
      <button class="btn btn-ghost btn-block" style="margin-top:16px; color:#f87171; border-color:#7f1d1d;" on:click={handleDeleteTeam}>Delete Team</button>
    {/if}
  {/if}
</div>
